import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { APIURL } from '../../../config';
import {
    Card, Box, Tabs, Tab, Typography, Grid, CircularProgress, Chip, Divider,
    Dialog, DialogTitle, DialogContent, DialogActions, Button
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CreditCardOffIcon from '@mui/icons-material/CreditCardOff';
import CreditCardIcon from '@mui/icons-material/CreditCard';

// ---- FHIR derivation helpers (mirror AH App BBMedicaidData.js) ----
const fmtDate = (v) => (v ? new Date(v).toLocaleDateString() : '—');
const fmtDateTime = (v) => (v ? new Date(v).toLocaleString() : '—');
const fmtMoney = (v) => `$${parseFloat(v || 0).toFixed(2)}`;
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const extDisplay = (arr, key) => arr?.find((e) => e.url?.includes(key))?.valueCoding?.display;
const extCode = (arr, key) => arr?.find((e) => e.url?.includes(key))?.valueCoding?.code;
const extDate = (arr, key) => arr?.find((e) => e.url?.includes(key))?.valueDate;

// Medicare-specific coverage extensions { key in url, label, how to read value }
const COVERAGE_EXTS = [
    { key: 'ms_cd', label: 'Medicare Status' },
    { key: 'orec', label: 'Original Reason for Entitlement' },
    { key: 'crec', label: 'Current Reason for Entitlement' },
    { key: 'esrd_ind', label: 'ESRD Indicator' },
    { key: 'a_trm_cd', label: 'Part A Status' },
    { key: 'b_trm_cd', label: 'Part B Status' },
    { key: 'dual_01', label: 'Dual Eligibility' },
    { key: 'buyin01', label: 'Buy-In Coverage' },
    { key: 'ptc_cntrct_id', label: 'Part C Contract ID', code: true },
    { key: 'ptdcntrct01', label: 'Part D Contract ID', code: true },
    { key: 'cstshr01', label: 'Cost Sharing Level' },
    { key: 'rdsind01', label: 'Retiree Drug Subsidy' },
    { key: 'rfrnc_yr', label: 'Reference Year', date: true },
];

function InfoRow({ label, value }) {
    return (
        <Grid container spacing={1} style={{ marginBottom: 6 }}>
            <Grid item xs={5}>
                <Typography variant="body2" color="textSecondary">{label}</Typography>
            </Grid>
            <Grid item xs={7}>
                <Typography variant="body2" style={{ wordBreak: 'break-word' }}>{value || '—'}</Typography>
            </Grid>
        </Grid>
    );
}

function TabPanel({ children, value, index }) {
    return value === index ? <Box style={{ padding: 12, overflowY: 'auto', flex: 1 }}>{children}</Box> : null;
}

function CenteredMessage({ icon, title, subtitle }) {
    return (
        <Box style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: 24, gap: 8 }}>
            {icon}
            <Typography variant="subtitle2" color="textSecondary">{title}</Typography>
            {subtitle && <Typography variant="caption" color="textSecondary">{subtitle}</Typography>}
        </Box>
    );
}

function PatientTab({ patient }) {
    if (!patient) return <Typography variant="body2" color="textSecondary">No patient information available.</Typography>;
    const name = [patient.name?.[0]?.given?.join(' '), patient.name?.[0]?.family].filter(Boolean).join(' ');
    const medicareNo = patient.identifier?.find((id) => id.type?.coding?.[0]?.code === 'MC')?.value;
    return (
        <>
            <InfoRow label="Name" value={name} />
            <InfoRow label="Date of Birth" value={fmtDate(patient.birthDate)} />
            <InfoRow label="Gender" value={cap(patient.gender)} />
            <InfoRow label="Medicare Number" value={medicareNo} />
            <InfoRow label="Postal Code" value={patient.address?.[0]?.postalCode} />
            <InfoRow label="Medicaid Status" value={extDisplay(patient.extension, 'dual_01')} />
            <InfoRow label="Reference Year" value={extDate(patient.extension, 'rfrnc_yr')} />
            <InfoRow label="Status" value={patient.deceasedBoolean ? 'Deceased' : 'Active'} />
            <InfoRow label="Last Updated" value={patient.meta?.lastUpdated ? fmtDateTime(patient.meta.lastUpdated) : null} />
        </>
    );
}

function CoverageTab({ coverage }) {
    const entries = coverage?.entry || [];
    if (!entries.length) return <Typography variant="body2" color="textSecondary">No coverage information available.</Typography>;
    return (
        <>
            {entries.map((item, idx) => {
                const r = item.resource || {};
                const planName = r.class?.find((c) => c.type?.coding?.[0]?.code === 'plan')?.value;
                return (
                    <Card key={r.id || idx} variant="outlined" style={{ padding: 10, marginBottom: 10, borderRadius: 8 }}>
                        <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                            <Typography variant="subtitle2">{planName || r.type?.coding?.[0]?.display || `Coverage ${idx + 1}`}</Typography>
                            {r.status && <Chip size="small" label={String(r.status).toUpperCase()} color={r.status === 'active' ? 'success' : 'default'} />}
                        </Box>
                        <InfoRow label="Coverage Period" value={`${fmtDate(r.period?.start)} – ${r.period?.end ? fmtDate(r.period.end) : 'Present'}`} />
                        <InfoRow label="Coverage ID" value={r.id} />
                        <InfoRow label="Subscriber ID" value={r.subscriberId} />
                        <InfoRow label="Relationship" value={r.relationship?.coding?.[0]?.display || r.relationship?.coding?.[0]?.code} />
                        <InfoRow label="Payor" value={r.payor?.[0]?.identifier?.value || r.payor?.[0]?.display} />
                        <InfoRow label="Coverage Type" value={r.type?.coding?.[0]?.display || r.type?.coding?.[0]?.code} />
                        {COVERAGE_EXTS.some((e) => (e.date ? extDate(r.extension, e.key) : e.code ? extCode(r.extension, e.key) : extDisplay(r.extension, e.key))) && (
                            <>
                                <Divider style={{ margin: '8px 0' }} />
                                <Typography variant="caption" color="textSecondary">Medicare Details</Typography>
                                <Box style={{ marginTop: 6 }}>
                                    {COVERAGE_EXTS.map((e) => {
                                        const val = e.date ? extDate(r.extension, e.key) : e.code ? extCode(r.extension, e.key) : extDisplay(r.extension, e.key);
                                        return val ? <InfoRow key={e.key} label={e.label} value={val} /> : null;
                                    })}
                                </Box>
                            </>
                        )}
                    </Card>
                );
            })}
        </>
    );
}

function ClaimsTab({ eob, onSelectClaim }) {
    const entries = eob?.entry || [];
    if (!entries.length) return <Typography variant="body2" color="textSecondary">No claims available.</Typography>;

    let totalCharges = 0;
    let totalPayments = 0;
    const claimTypes = {};
    entries.forEach((item) => {
        const r = item.resource || {};
        if (r.total?.[0]?.amount?.value) totalCharges += parseFloat(r.total[0].amount.value);
        if (r.payment?.amount?.value) totalPayments += parseFloat(r.payment.amount.value);
        const t = r.type?.coding?.[0]?.display || r.subType?.text || 'Other';
        claimTypes[t] = (claimTypes[t] || 0) + 1;
    });

    return (
        <>
            <Card variant="outlined" style={{ padding: 10, marginBottom: 10, borderRadius: 8, background: '#F1F8FC' }}>
                <Grid container>
                    <Grid item xs={4}><Typography variant="caption" color="textSecondary">Claims</Typography><Typography variant="subtitle2">{entries.length}</Typography></Grid>
                    <Grid item xs={4}><Typography variant="caption" color="textSecondary">Total Charges</Typography><Typography variant="subtitle2">{fmtMoney(totalCharges)}</Typography></Grid>
                    <Grid item xs={4}><Typography variant="caption" color="textSecondary">Total Payments</Typography><Typography variant="subtitle2">{fmtMoney(totalPayments)}</Typography></Grid>
                </Grid>
                <Box style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {Object.entries(claimTypes).map(([t, count]) => (
                        <Chip key={t} size="small" variant="outlined" label={`${t}: ${count}`} />
                    ))}
                </Box>
            </Card>

            {entries.map((item, idx) => {
                const r = item.resource || {};
                const type = r.type?.coding?.[0]?.display || r.subType?.text || 'Claim';
                return (
                    <Card
                        key={r.id || idx}
                        variant="outlined"
                        onClick={() => r.id && onSelectClaim(r.id)}
                        style={{ padding: 10, marginBottom: 8, borderRadius: 8, cursor: r.id ? 'pointer' : 'default' }}
                    >
                        <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box>
                                <Typography variant="body2" style={{ fontWeight: 500 }}>{type}</Typography>
                                <Typography variant="caption" color="textSecondary">{fmtDate(r.billablePeriod?.start || r.created)}</Typography>
                            </Box>
                            <Box style={{ textAlign: 'right' }}>
                                <Typography variant="body2">{fmtMoney(r.total?.[0]?.amount?.value)}</Typography>
                                <Typography variant="caption" color="textSecondary">Paid {fmtMoney(r.payment?.amount?.value)}</Typography>
                            </Box>
                        </Box>
                    </Card>
                );
            })}
        </>
    );
}

function ClaimDetailDialog({ open, onClose, loading, claim }) {
    const renderBody = () => {
        if (loading) {
            return <Box style={{ display: 'flex', justifyContent: 'center', padding: 32 }}><CircularProgress size={28} /></Box>;
        }
        if (!claim) {
            return <Typography variant="body2" color="textSecondary">Unable to load claim details.</Typography>;
        }
        const admissionType = claim.supportingInfo
            ?.find((i) => i.category?.coding?.[0]?.code === 'admtype')?.code?.coding?.[0]?.display?.split(' - ')[0];
        const drg = claim.supportingInfo
            ?.find((i) => i.code?.coding?.[0]?.system?.includes('clm_drg_cd'))?.code?.coding?.[0]?.code;
        return (
            <>
                <InfoRow label="Claim ID" value={claim.id} />
                <InfoRow label="Status" value={cap(claim.status)} />
                <InfoRow label="Provider" value={claim.contained?.[0]?.name} />
                <InfoRow label="Facility Type" value={claim.facility?.extension?.[0]?.valueCoding?.display} />
                <InfoRow label="Admission Type" value={admissionType} />
                <InfoRow label="DRG Code" value={drg} />

                {claim.diagnosis?.length > 0 && (
                    <>
                        <Divider style={{ margin: '10px 0' }} />
                        <Typography variant="subtitle2" gutterBottom>Diagnosis Codes</Typography>
                        {claim.diagnosis.map((diag, i) => {
                            const code = diag.diagnosisCodeableConcept?.coding?.[0];
                            const type = diag.type?.[0]?.coding?.[0]?.display;
                            return (
                                <Box key={i} style={{ marginBottom: 6 }}>
                                    <Box style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <Typography variant="body2">{code?.display || code?.code || 'N/A'}</Typography>
                                        {type === 'Principal Diagnosis' && <Chip size="small" color="primary" label="P" />}
                                    </Box>
                                    <Typography variant="caption" color="textSecondary">ICD-10: {code?.code || '—'} · {type || '—'}</Typography>
                                </Box>
                            );
                        })}
                    </>
                )}

                <Divider style={{ margin: '10px 0' }} />
                <Typography variant="subtitle2" gutterBottom>Financial Summary</Typography>
                {claim.total?.[0] && (
                    <InfoRow
                        label={claim.total[0].category?.coding?.[0]?.display || 'Total Charge'}
                        value={fmtMoney(claim.total[0].amount?.value)}
                    />
                )}
                {claim.payment?.amount && <InfoRow label="Payment Amount" value={fmtMoney(claim.payment.amount.value)} />}

                {claim.item?.length > 0 && (
                    <>
                        <Divider style={{ margin: '10px 0' }} />
                        <Typography variant="subtitle2" gutterBottom>Service Lines</Typography>
                        {claim.item.map((it, i) => (
                            <Card key={i} variant="outlined" style={{ padding: 8, marginBottom: 8, borderRadius: 8 }}>
                                <Typography variant="body2" style={{ fontWeight: 500 }}>
                                    {it.revenue?.coding?.[0]?.display || it.productOrService?.coding?.[0]?.display || `Service ${i + 1}`}
                                </Typography>
                                {it.revenue?.coding?.[0]?.code && <Typography variant="caption" color="textSecondary" display="block">Revenue Code: {it.revenue.coding[0].code}</Typography>}
                                {it.productOrService?.coding?.[0]?.code && <Typography variant="caption" color="textSecondary" display="block">HCPCS Code: {it.productOrService.coding[0].code}</Typography>}
                                {it.adjudication?.map((adj, ai) => (
                                    <InfoRow
                                        key={ai}
                                        label={adj.category?.coding?.[0]?.display?.replace('Amount', '').trim() || 'Amount'}
                                        value={fmtMoney(adj.amount?.value)}
                                    />
                                ))}
                            </Card>
                        ))}
                    </>
                )}
            </>
        );
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Claim Details</DialogTitle>
            <DialogContent dividers>{renderBody()}</DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}

function MedicareSection({ custEmail, userEmail }) {
    const [activeTab, setActiveTab] = useState(0);
    const [state, setState] = useState({ loading: true, hasAccess: false, connected: false, error: null });
    const [data, setData] = useState(null);

    const [claimOpen, setClaimOpen] = useState(false);
    const [claimLoading, setClaimLoading] = useState(false);
    const [claim, setClaim] = useState(null);

    const fetchData = useCallback(async () => {
        if (!custEmail || !userEmail) {
            setState({ loading: false, hasAccess: false, connected: false, error: null });
            return;
        }
        setState((s) => ({ ...s, loading: true }));
        try {
            const res = await axios.get(APIURL + '/ability_hub/medicare', {
                params: { userEmail, custEmail },
            });
            setData(res.data?.data || null);
            setState({ loading: false, hasAccess: !!res.data?.hasAccess, connected: !!res.data?.connected, error: res.data?.error || null });
        } catch (err) {
            const status = err.response?.status;
            if (status === 403) {
                setState({ loading: false, hasAccess: false, connected: false, error: null });
            } else {
                setState({ loading: false, hasAccess: true, connected: false, error: 'Failed to load Medicare data.' });
            }
        }
    }, [custEmail, userEmail]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const openClaim = async (claimId) => {
        setClaim(null);
        setClaimOpen(true);
        setClaimLoading(true);
        try {
            const res = await axios.get(APIURL + `/ability_hub/medicare/claim/${claimId}`, {
                params: { userEmail, custEmail },
            });
            setClaim(res.data?.data || null);
        } catch (err) {
            setClaim(null);
        }
        setClaimLoading(false);
    };

    const header = (
        <Box style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px', borderBottom: '1px solid #e0e0e0' }}>
            <CreditCardIcon fontSize="small" color="primary" />
            <Typography variant="subtitle2">Medicare Data</Typography>
        </Box>
    );

    const renderContent = () => {
        if (state.loading) {
            return <Box style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}><CircularProgress size={26} /></Box>;
        }
        if (!state.hasAccess) {
            return (
                <CenteredMessage
                    icon={<LockOutlinedIcon style={{ fontSize: 36, color: '#9e9e9e' }} />}
                    title="No access to Medicare data"
                    subtitle="You don't have Health permission for this client's AbilityHub record."
                />
            );
        }
        if (!state.connected) {
            return (
                <CenteredMessage
                    icon={<CreditCardOffIcon style={{ fontSize: 36, color: '#9e9e9e' }} />}
                    title="Medicare not connected"
                    subtitle="This client has not connected Medicare in the AbilityHub app yet."
                />
            );
        }
        if (state.error) {
            return <CenteredMessage icon={<CreditCardOffIcon style={{ fontSize: 36, color: '#9e9e9e' }} />} title={state.error} />;
        }
        return (
            <>
                <Box style={{ paddingLeft: 8, paddingRight: 8, borderBottom: '1px solid #e0e0e0' }}>
                    <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} variant="fullWidth">
                        <Tab label="PATIENT" value={0} />
                        <Tab label="COVERAGE" value={1} />
                        <Tab label="CLAIMS" value={2} />
                    </Tabs>
                </Box>
                <TabPanel value={activeTab} index={0}><PatientTab patient={data?.patient} /></TabPanel>
                <TabPanel value={activeTab} index={1}><CoverageTab coverage={data?.coverage} /></TabPanel>
                <TabPanel value={activeTab} index={2}><ClaimsTab eob={data?.eob} onSelectClaim={openClaim} /></TabPanel>
            </>
        );
    };

    return (
        <Card elevation={3} style={{ height: '100%', borderRadius: '10px', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {header}
            {renderContent()}
            <ClaimDetailDialog open={claimOpen} onClose={() => setClaimOpen(false)} loading={claimLoading} claim={claim} />
        </Card>
    );
}

export default MedicareSection;
